/**
 * Mist language service — shared validation pipeline for editor diagnostics,
 * build, and deploy preflight.
 *
 * Implements four diagnostic layers:
 *   A – Parse (indentation, tokens, structure)
 *   B – Compile / Type (types, identifiers, signatures)
 *   C – Static Analysis (locked funds, conservation, determinism)
 *   D – Deploy preflight (file type, wallet, project state)
 *
 * This module runs entirely in the browser. It mirrors the real
 * rivellum-mist-parser/compiler logic as closely as feasible in
 * TypeScript to provide instant editor feedback.
 */

/* ================================================================== */
/*  Diagnostic types                                                  */
/* ================================================================== */

export type DiagnosticSeverity = 'error' | 'warning' | 'info' | 'hint';

export type DiagnosticCategory = 'syntax' | 'compiler' | 'analysis' | 'deploy';

export interface MistDiagnostic {
  /** 1-based line number. */
  line: number;
  /** 1-based column. */
  column: number;
  /** 1-based end line (defaults to line). */
  endLine: number;
  /** 1-based end column (defaults to end of line). */
  endColumn: number;
  severity: DiagnosticSeverity;
  category: DiagnosticCategory;
  message: string;
  /** Optional error code (e.g. MIST_E_LOCKED_FUNDS). */
  code?: string;
}

/* ================================================================== */
/*  Token types (mirrors rivellum-mist-parser lexer.rs)                */
/* ================================================================== */

type TokenKind =
  | 'Contract' | 'State' | 'Function' | 'Event'
  | 'Let' | 'If' | 'Else' | 'For' | 'While' | 'Return'
  | 'Require' | 'Assert' | 'Emit' | 'Policy' | 'CanMint' | 'CanBurn'
  | 'Hold' | 'Pay' | 'Release' | 'Refund' | 'Split' | 'Mint' | 'Burn'
  | 'Now' | 'Oracle' | 'Caller' | 'Intent'
  | 'Int' | 'Bool' | 'String' | 'Address' | 'Timestamp' | 'Value'
  | 'U128' | 'U64' | 'Asset' | 'VaultRef'
  | 'IntLiteral' | 'StringLiteral' | 'BoolLiteral'
  | 'Plus' | 'Minus' | 'Star' | 'Slash' | 'Percent'
  | 'Equal' | 'EqualEqual' | 'NotEqual'
  | 'Less' | 'LessEqual' | 'Greater' | 'GreaterEqual'
  | 'And' | 'Or' | 'Not'
  | 'Dot' | 'Colon' | 'Arrow' | 'Comma'
  | 'LeftParen' | 'RightParen' | 'LeftBracket' | 'RightBracket'
  | 'LeftBrace' | 'RightBrace'
  | 'Indent' | 'Dedent' | 'Newline' | 'Eof'
  | 'Identifier'
  // Context keywords used as modifiers in value ops
  | 'AssetMod' | 'From' | 'To' | 'Into' | 'Vault' | 'Authority'
  | 'True' | 'False';

interface Token {
  kind: TokenKind;
  value: string;
  line: number;
  column: number;
}

/* ================================================================== */
/*  Lexer (mirrors rivellum-mist-parser/src/lexer.rs)                 */
/* ================================================================== */

const KEYWORD_MAP: Record<string, TokenKind> = {
  contract: 'Contract', state: 'State', function: 'Function', event: 'Event',
  let: 'Let', if: 'If', else: 'Else', for: 'For', while: 'While',
  return: 'Return', require: 'Require', assert: 'Assert', emit: 'Emit',
  policy: 'Policy', can_mint: 'CanMint', can_burn: 'CanBurn',
  hold: 'Hold', pay: 'Pay', release: 'Release', refund: 'Refund',
  split: 'Split', mint: 'Mint', burn: 'Burn',
  now: 'Now', oracle: 'Oracle', caller: 'Caller', intent: 'Intent',
  Int: 'Int', Bool: 'Bool', String: 'String', Address: 'Address',
  Timestamp: 'Timestamp', Value: 'Value', U128: 'U128', U64: 'U64',
  Asset: 'Asset', VaultRef: 'VaultRef',
  true: 'True', false: 'False',
  asset: 'AssetMod', from: 'From', to: 'To', into: 'Into',
  vault: 'Vault', authority: 'Authority',
};

function tokenize(source: string): { tokens: Token[]; errors: MistDiagnostic[] } {
  const tokens: Token[] = [];
  const errors: MistDiagnostic[] = [];
  const lines = source.split('\n');
  const indentStack: number[] = [0];

  for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
    const rawLine = lines[lineIdx];
    const lineNum = lineIdx + 1;

    // Skip blank lines and comment-only lines
    const stripped = rawLine.replace(/\s+$/, '');
    if (stripped.length === 0 || /^\s*#/.test(stripped)) {
      tokens.push({ kind: 'Newline', value: '\n', line: lineNum, column: 1 });
      continue;
    }

    // Calculate indentation
    const indentMatch = rawLine.match(/^( *)/);
    const indent = indentMatch ? indentMatch[1].length : 0;

    // Check for tab characters
    if (/\t/.test(rawLine)) {
      errors.push({
        line: lineNum, column: 1, endLine: lineNum, endColumn: rawLine.length + 1,
        severity: 'error', category: 'syntax',
        message: 'Tab characters are not allowed in Mist. Use spaces for indentation.',
        code: 'MIST_TAB',
      });
    }

    // Check for odd indentation (Mist uses 4-space indentation)
    if (indent % 4 !== 0) {
      errors.push({
        line: lineNum, column: 1, endLine: lineNum, endColumn: indent + 1,
        severity: 'error', category: 'syntax',
        message: `Invalid indentation: ${indent} spaces. Mist uses 4-space indentation. Expected ${Math.round(indent / 4) * 4} spaces.`,
        code: 'MIST_INDENT',
      });
    }

    const currentIndent = indentStack[indentStack.length - 1];

    if (indent > currentIndent) {
      indentStack.push(indent);
      tokens.push({ kind: 'Indent', value: '', line: lineNum, column: 1 });
    } else if (indent < currentIndent) {
      while (indentStack.length > 1 && indentStack[indentStack.length - 1] > indent) {
        indentStack.pop();
        tokens.push({ kind: 'Dedent', value: '', line: lineNum, column: 1 });
      }
      if (indentStack[indentStack.length - 1] !== indent) {
        errors.push({
          line: lineNum, column: 1, endLine: lineNum, endColumn: indent + 1,
          severity: 'error', category: 'syntax',
          message: `Inconsistent dedent: indentation level ${indent} does not match any enclosing block.`,
          code: 'MIST_DEDENT',
        });
      }
    }

    // Tokenize the content of the line
    let col = indent;
    const content = rawLine;
    while (col < content.length) {
      const ch = content[col];

      // Skip spaces (already handled indentation)
      if (ch === ' ') { col++; continue; }

      // Comment
      if (ch === '#') { break; }

      const colNum = col + 1; // 1-based

      // String literals
      if (ch === '"' || ch === "'") {
        const quote = ch;
        let str = '';
        let j = col + 1;
        let terminated = false;
        while (j < content.length) {
          if (content[j] === '\\' && j + 1 < content.length) {
            str += content[j] + content[j + 1];
            j += 2;
          } else if (content[j] === quote) {
            terminated = true;
            j++;
            break;
          } else {
            str += content[j];
            j++;
          }
        }
        if (!terminated) {
          errors.push({
            line: lineNum, column: colNum, endLine: lineNum, endColumn: content.length + 1,
            severity: 'error', category: 'syntax',
            message: `Unterminated string literal. Expected closing ${quote}.`,
            code: 'MIST_STRING',
          });
        }
        tokens.push({ kind: 'StringLiteral', value: str, line: lineNum, column: colNum });
        col = j;
        continue;
      }

      // Number literals
      if (/\d/.test(ch)) {
        let num = '';
        let j = col;
        while (j < content.length && /\d/.test(content[j])) {
          num += content[j]; j++;
        }
        // Check for invalid suffix (e.g. 123abc)
        if (j < content.length && /[a-zA-Z_]/.test(content[j])) {
          let badSuffix = num;
          while (j < content.length && /[a-zA-Z_\d]/.test(content[j])) {
            badSuffix += content[j]; j++;
          }
          errors.push({
            line: lineNum, column: colNum, endLine: lineNum, endColumn: col + badSuffix.length + 1,
            severity: 'error', category: 'syntax',
            message: `Invalid number literal "${badSuffix}". Numbers cannot have alphabetic suffixes.`,
            code: 'MIST_NUMBER',
          });
          tokens.push({ kind: 'IntLiteral', value: num, line: lineNum, column: colNum });
          col = j;
          continue;
        }
        tokens.push({ kind: 'IntLiteral', value: num, line: lineNum, column: colNum });
        col = j;
        continue;
      }

      // Identifiers and keywords
      if (/[a-zA-Z_]/.test(ch)) {
        let ident = '';
        let j = col;
        while (j < content.length && /[a-zA-Z_\d]/.test(content[j])) {
          ident += content[j]; j++;
        }
        const kind = KEYWORD_MAP[ident] ?? 'Identifier';
        tokens.push({ kind, value: ident, line: lineNum, column: colNum });
        col = j;
        continue;
      }

      // Two-character operators
      if (col + 1 < content.length) {
        const twoChar = content[col] + content[col + 1];
        const twoCharMap: Record<string, TokenKind> = {
          '==': 'EqualEqual', '!=': 'NotEqual', '<=': 'LessEqual',
          '>=': 'GreaterEqual', '&&': 'And', '||': 'Or', '->': 'Arrow',
        };
        if (twoCharMap[twoChar]) {
          tokens.push({ kind: twoCharMap[twoChar], value: twoChar, line: lineNum, column: colNum });
          col += 2;
          continue;
        }
      }

      // Single-character operators and delimiters
      const singleMap: Record<string, TokenKind> = {
        '+': 'Plus', '-': 'Minus', '*': 'Star', '/': 'Slash', '%': 'Percent',
        '=': 'Equal', '<': 'Less', '>': 'Greater', '!': 'Not',
        '.': 'Dot', ':': 'Colon', ',': 'Comma',
        '(': 'LeftParen', ')': 'RightParen',
        '[': 'LeftBracket', ']': 'RightBracket',
        '{': 'LeftBrace', '}': 'RightBrace',
      };
      if (singleMap[ch]) {
        tokens.push({ kind: singleMap[ch], value: ch, line: lineNum, column: colNum });
        col++;
        continue;
      }

      // Unknown character
      errors.push({
        line: lineNum, column: colNum, endLine: lineNum, endColumn: colNum + 1,
        severity: 'error', category: 'syntax',
        message: `Unexpected character '${ch}'.`,
        code: 'MIST_UNEXPECTED_CHAR',
      });
      col++;
    }

    tokens.push({ kind: 'Newline', value: '\n', line: lineNum, column: content.length + 1 });
  }

  // Emit remaining dedents
  while (indentStack.length > 1) {
    indentStack.pop();
    tokens.push({ kind: 'Dedent', value: '', line: lines.length, column: 1 });
  }

  tokens.push({ kind: 'Eof', value: '', line: lines.length + 1, column: 1 });

  return { tokens, errors };
}

/* ================================================================== */
/*  Parser (mirrors rivellum-mist-parser/src/parser.rs)               */
/* ================================================================== */

interface ParseContext {
  tokens: Token[];
  pos: number;
  errors: MistDiagnostic[];
}

function peek(ctx: ParseContext): Token {
  skipNewlines(ctx);
  return ctx.tokens[ctx.pos] ?? { kind: 'Eof', value: '', line: 0, column: 0 };
}

function advance(ctx: ParseContext): Token {
  skipNewlines(ctx);
  const tok = ctx.tokens[ctx.pos] ?? { kind: 'Eof', value: '', line: 0, column: 0 };
  ctx.pos++;
  return tok;
}

function skipNewlines(ctx: ParseContext): void {
  while (ctx.pos < ctx.tokens.length && ctx.tokens[ctx.pos].kind === 'Newline') {
    ctx.pos++;
  }
}

function expect(ctx: ParseContext, kind: TokenKind, description: string): Token | null {
  const tok = peek(ctx);
  if (tok.kind !== kind) {
    ctx.errors.push({
      line: tok.line, column: tok.column, endLine: tok.line, endColumn: tok.column + (tok.value.length || 1),
      severity: 'error', category: 'syntax',
      message: `Expected ${description}, but found ${tok.kind === 'Eof' ? 'end of file' : `'${tok.value}'`}.`,
      code: 'MIST_EXPECTED',
    });
    return null;
  }
  return advance(ctx);
}

function isTypeToken(kind: TokenKind): boolean {
  return ['Int', 'Bool', 'String', 'Address', 'Timestamp', 'Value', 'U128', 'U64', 'Asset', 'VaultRef'].includes(kind);
}

/** Collected declarations for duplicate detection. */
interface ContractInfo {
  name: string;
  states: { name: string; line: number }[];
  functions: { name: string; line: number }[];
  events: { name: string; line: number }[];
}

function parseContract(ctx: ParseContext): ContractInfo | null {
  const contractTok = peek(ctx);
  if (contractTok.kind !== 'Contract') {
    ctx.errors.push({
      line: contractTok.line, column: contractTok.column,
      endLine: contractTok.line, endColumn: contractTok.column + (contractTok.value.length || 1),
      severity: 'error', category: 'syntax',
      message: `Expected 'contract' declaration at the top level, but found '${contractTok.value || 'end of file'}'.`,
      code: 'MIST_NO_CONTRACT',
    });
    return null;
  }
  advance(ctx); // consume 'contract'

  const nameTok = expect(ctx, 'Identifier', 'contract name');
  if (!nameTok) return null;

  const info: ContractInfo = {
    name: nameTok.value,
    states: [],
    functions: [],
    events: [],
  };

  // Expect indent
  if (peek(ctx).kind !== 'Indent') {
    ctx.errors.push({
      line: nameTok.line, column: nameTok.column + nameTok.value.length,
      endLine: nameTok.line + 1, endColumn: 1,
      severity: 'error', category: 'syntax',
      message: `Expected an indented block after 'contract ${nameTok.value}'.`,
      code: 'MIST_EXPECTED_INDENT',
    });
    return info;
  }
  advance(ctx); // consume Indent

  // Parse body
  while (peek(ctx).kind !== 'Dedent' && peek(ctx).kind !== 'Eof') {
    const tok = peek(ctx);

    if (tok.kind === 'State') {
      advance(ctx);
      const sName = expect(ctx, 'Identifier', 'state variable name');
      if (sName) {
        info.states.push({ name: sName.value, line: sName.line });
        expect(ctx, 'Colon', "':' after state name");
        // Consume type
        const typeTok = peek(ctx);
        if (isTypeToken(typeTok.kind)) {
          advance(ctx);
        } else {
          ctx.errors.push({
            line: typeTok.line, column: typeTok.column,
            endLine: typeTok.line, endColumn: typeTok.column + (typeTok.value.length || 1),
            severity: 'error', category: 'compiler',
            message: `Expected a type (Int, Bool, String, Address, Timestamp, Value, U128, U64, Asset, VaultRef) after ':', but found '${typeTok.value}'.`,
            code: 'MIST_INVALID_TYPE',
          });
          advance(ctx);
        }
      }
    } else if (tok.kind === 'Event') {
      advance(ctx);
      const eName = expect(ctx, 'Identifier', 'event name');
      if (eName) {
        info.events.push({ name: eName.value, line: eName.line });
        // Skip optional parameter list
        if (peek(ctx).kind === 'LeftParen') {
          let depth = 0;
          do {
            const t = advance(ctx);
            if (t.kind === 'LeftParen') depth++;
            if (t.kind === 'RightParen') depth--;
          } while (depth > 0 && peek(ctx).kind !== 'Eof');
        }
      }
    } else if (tok.kind === 'Function') {
      advance(ctx);
      const fName = expect(ctx, 'Identifier', 'function name');
      if (fName) {
        info.functions.push({ name: fName.value, line: fName.line });
        // Skip params and return type to the Indent
        while (peek(ctx).kind !== 'Indent' && peek(ctx).kind !== 'Eof' && peek(ctx).kind !== 'Dedent') {
          advance(ctx);
        }
        // Skip body
        if (peek(ctx).kind === 'Indent') {
          advance(ctx); // consume Indent
          let depth = 1;
          while (depth > 0 && peek(ctx).kind !== 'Eof') {
            const t = advance(ctx);
            if (t.kind === 'Indent') depth++;
            if (t.kind === 'Dedent') depth--;
          }
        } else {
          ctx.errors.push({
            line: fName.line, column: fName.column + fName.value.length,
            endLine: fName.line + 1, endColumn: 1,
            severity: 'error', category: 'syntax',
            message: `Expected an indented function body after 'function ${fName.value}'.`,
            code: 'MIST_EXPECTED_INDENT',
          });
        }
      }
    } else if (tok.kind === 'Policy') {
      advance(ctx);
      // Skip to Indent and consume body
      while (peek(ctx).kind !== 'Indent' && peek(ctx).kind !== 'Eof' && peek(ctx).kind !== 'Dedent') {
        advance(ctx);
      }
      if (peek(ctx).kind === 'Indent') {
        advance(ctx);
        let depth = 1;
        while (depth > 0 && peek(ctx).kind !== 'Eof') {
          const t = advance(ctx);
          if (t.kind === 'Indent') depth++;
          if (t.kind === 'Dedent') depth--;
        }
      }
    } else {
      // Unexpected token in contract body
      ctx.errors.push({
        line: tok.line, column: tok.column,
        endLine: tok.line, endColumn: tok.column + (tok.value.length || 1),
        severity: 'error', category: 'syntax',
        message: `Unexpected '${tok.value}' in contract body. Expected 'state', 'function', 'event', or 'policy'.`,
        code: 'MIST_UNEXPECTED',
      });
      advance(ctx);
    }
  }

  // Consume final Dedent
  if (peek(ctx).kind === 'Dedent') advance(ctx);

  return info;
}

/* ================================================================== */
/*  Statement-level validation (inside function bodies)               */
/* ================================================================== */

/** Quick heuristic checks on function body lines. */
function validateFunctionBodies(source: string, diagnostics: MistDiagnostic[]): void {
  const lines = source.split('\n');

  let inFunction = false;
  let functionIndent = -1;
  let prevLineWasBlockStarter = false;
  let prevLineIndent = 0;

  // Common misspellings → correct keyword
  const KEYWORD_TYPOS: Record<string, string> = {
    requre: 'require', reqire: 'require', requir: 'require', reuire: 'require', reqiure: 'require',
    requrie: 'require', rrequire: 'require', requier: 'require',
    assrt: 'assert', asert: 'assert', assrrt: 'assert', asssert: 'assert',
    retrun: 'return', retunr: 'return', reutrn: 'return', retrn: 'return',
    fucntion: 'function', funtion: 'function', funciton: 'function',
    fal: 'false', flase: 'false', fasle: 'false', fales: 'false',
    ture: 'true', treu: 'true', tru: 'true',
    assest: 'asset', asste: 'asset', asest: 'asset',
    pya: 'pay',
    hol: 'hold', hodl: 'hold',
    relase: 'release', relaese: 'release', realease: 'release',
    refnd: 'refund', refudn: 'refund',
    mnt: 'mint', mitn: 'mint',
    brn: 'burn', brnu: 'burn',
    emt: 'emit', emti: 'emit',
    compted: 'completed', complted: 'completed',
  };

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const lineNum = i + 1;
    const stripped = raw.trimStart();
    if (stripped.length === 0 || stripped.startsWith('#')) {
      continue;
    }

    const indent = raw.length - raw.trimStart().length;

    // Detect function start
    if (stripped.startsWith('function ')) {
      inFunction = true;
      functionIndent = indent;
    }

    // Check: block starter should be followed by indented content
    if (prevLineWasBlockStarter) {
      if (indent <= prevLineIndent && stripped.length > 0) {
        diagnostics.push({
          line: lineNum, column: 1, endLine: lineNum, endColumn: indent + 1,
          severity: 'error', category: 'syntax',
          message: `Expected an indented block after line ${lineNum - 1}.`,
          code: 'MIST_EXPECTED_INDENT',
        });
      }
    }

    // Check for common statement-level issues
    if (inFunction && indent > functionIndent) {
      // Check for misspelled keywords
      const firstWordMatch = stripped.match(/^([a-zA-Z_]\w*)/);
      if (firstWordMatch) {
        const firstWord = firstWordMatch[1].toLowerCase();
        const correction = KEYWORD_TYPOS[firstWord];
        if (correction) {
          diagnostics.push({
            line: lineNum, column: indent + 1, endLine: lineNum, endColumn: indent + firstWordMatch[1].length + 1,
            severity: 'error', category: 'syntax',
            message: `Unknown identifier '${firstWordMatch[1]}'. Did you mean '${correction}'?`,
            code: 'MIST_TYPO',
          });
        }
      }

      // Check for misspelled words anywhere on the line (not just first word)
      const allWords = stripped.match(/\b([a-zA-Z_]\w*)\b/g) ?? [];
      for (const word of allWords) {
        const lw = word.toLowerCase();
        if (lw === firstWordMatch?.[1]?.toLowerCase()) continue; // already checked
        const corr = KEYWORD_TYPOS[lw];
        if (corr) {
          const wordCol = raw.indexOf(word) + 1;
          diagnostics.push({
            line: lineNum, column: wordCol, endLine: lineNum, endColumn: wordCol + word.length,
            severity: 'error', category: 'syntax',
            message: `Unknown identifier '${word}'. Did you mean '${corr}'?`,
            code: 'MIST_TYPO',
          });
        }
      }

      // require/assert should have an expression
      if (/^\s*(require|assert)\s*$/.test(raw)) {
        diagnostics.push({
          line: lineNum, column: indent + 1, endLine: lineNum, endColumn: raw.length + 1,
          severity: 'error', category: 'compiler',
          message: `'${stripped}' requires a condition expression. Example: require amount > 0`,
          code: 'MIST_MISSING_CONDITION',
        });
      }

      // emit should have event name
      if (/^\s*emit\s*$/.test(raw)) {
        diagnostics.push({
          line: lineNum, column: indent + 1, endLine: lineNum, endColumn: raw.length + 1,
          severity: 'error', category: 'compiler',
          message: `'emit' requires an event name. Example: emit Transfer(from, to, amount)`,
          code: 'MIST_MISSING_EVENT',
        });
      }

      // let without =
      if (/^\s*let\s+\w+\s*$/.test(raw) || /^\s*let\s+\w+\s*:\s*\w+\s*$/.test(raw)) {
        diagnostics.push({
          line: lineNum, column: indent + 1, endLine: lineNum, endColumn: raw.length + 1,
          severity: 'error', category: 'compiler',
          message: `Variable declaration requires an initializer. Example: let x = 0`,
          code: 'MIST_LET_NO_INIT',
        });
      }

      // bounded collection declarations require explicit capacity
      if (/^\s*(state|let)\s+\w+\s*:\s*(List|Set|Map)\s*$/.test(raw)) {
        diagnostics.push({
          line: lineNum, column: indent + 1, endLine: lineNum, endColumn: raw.length + 1,
          severity: 'error', category: 'compiler',
          message: 'Bounded collections require explicit capacity. Use List<T, CAP>, Set<T, CAP>, or Map<K, V, CAP>.',
          code: 'MIST_T1015',
        });
      }

      // capacity must be > 0
      if (/\b(List|Set)\s*<[^>]*,\s*0\s*>/.test(raw) || /\bMap\s*<[^>]*,\s*0\s*>/.test(raw)) {
        diagnostics.push({
          line: lineNum, column: indent + 1, endLine: lineNum, endColumn: raw.length + 1,
          severity: 'error', category: 'compiler',
          message: 'Bounded collection capacity must be greater than zero.',
          code: 'MIST_T1016',
        });
      }

      // while loops are forbidden in deterministic bounded subset
      if (/^\s*while\b/.test(raw)) {
        diagnostics.push({
          line: lineNum, column: indent + 1, endLine: lineNum, endColumn: raw.length + 1,
          severity: 'error', category: 'compiler',
          message: 'while loops are forbidden in Mist. Use bounded for loops: for i in 0..N or for i in 0..list_len(collection).',
          code: 'MIST_T1019',
        });
      }

      // heuristic unprovable bound check for for-loops
      const forUnprovable = raw.match(/^\s*for\s+\w+\s+in\s+[^.]+\.\.\s*([a-zA-Z_]\w*)\s*$/);
      if (forUnprovable && !/\blist_len\(|\bset_len\(|\bmap_len\(/.test(raw)) {
        diagnostics.push({
          line: lineNum, column: indent + 1, endLine: lineNum, endColumn: raw.length + 1,
          severity: 'warning', category: 'compiler',
          message: 'Loop bound may be unprovable. Prefer literal bounds or collection length builtins.',
          code: 'MIST_T1019',
        });
      }

      // set/map iteration restrictions in v1 (heuristic hints)
      if (/^\s*for\s+\w+\s+in\s+\w+/.test(raw) && /\b(set|members|active_orders)\b/.test(raw.toLowerCase())) {
        diagnostics.push({
          line: lineNum, column: indent + 1, endLine: lineNum, endColumn: raw.length + 1,
          severity: 'error', category: 'compiler',
          message: 'Set iteration is forbidden in v1. Use set_contains/set_insert and explicit List for ordered traversal.',
          code: 'MIST_T1017',
        });
      }
      if (/^\s*for\s+\w+\s+in\s+\w+/.test(raw) && /\b(map|registry|cache)\b/.test(raw.toLowerCase())) {
        diagnostics.push({
          line: lineNum, column: indent + 1, endLine: lineNum, endColumn: raw.length + 1,
          severity: 'error', category: 'compiler',
          message: 'Map iteration is forbidden in v1. Use map_get/map_put/map_contains with explicit List<Key> for order.',
          code: 'MIST_T1018',
        });
      }

      // pay without destination
      if (/^\s*pay\s+\S+\s+asset\s+\S+\s*$/.test(raw)) {
        diagnostics.push({
          line: lineNum, column: indent + 1, endLine: lineNum, endColumn: raw.length + 1,
          severity: 'warning', category: 'compiler',
          message: `'pay' requires a destination. Example: pay 100 asset "RVL" to recipient`,
          code: 'MIST_PAY_NO_DEST',
        });
      }

      // hold without from
      if (/^\s*hold\s+\S+\s+asset\s+\S+\s*$/.test(raw)) {
        diagnostics.push({
          line: lineNum, column: indent + 1, endLine: lineNum, endColumn: raw.length + 1,
          severity: 'warning', category: 'compiler',
          message: `'hold' requires a source address. Example: hold 100 asset "RVL" from sender`,
          code: 'MIST_HOLD_NO_FROM',
        });
      }

      // return in contract body (not in function) — detected only if indent matches contract level
      if (/^\s*return\b/.test(raw) && indent <= functionIndent + 4 && !inFunction) {
        diagnostics.push({
          line: lineNum, column: indent + 1, endLine: lineNum, endColumn: raw.length + 1,
          severity: 'error', category: 'compiler',
          message: `'return' can only appear inside a function body.`,
          code: 'MIST_RETURN_OUTSIDE',
        });
      }
    }

    // Track block starters for indent check
    prevLineWasBlockStarter = /^\s*(contract|function|if|else|for|while|policy)\b/.test(raw);
    prevLineIndent = indent;
  }
}

/* ================================================================== */
/*  Static analysis (mirrors rivellum-mist-compiler static checks)    */
/* ================================================================== */

function runStaticAnalysis(source: string, contractInfo: ContractInfo | null, diagnostics: MistDiagnostic[]): void {
  if (!contractInfo) return;

  const lines = source.split('\n');

  // Check for duplicate state names
  const stateNames = new Map<string, number>();
  for (const s of contractInfo.states) {
    const existing = stateNames.get(s.name);
    if (existing !== undefined) {
      diagnostics.push({
        line: s.line, column: 1, endLine: s.line, endColumn: 100,
        severity: 'error', category: 'analysis',
        message: `Duplicate state variable '${s.name}'. First declared on line ${existing}.`,
        code: 'MIST_DUPLICATE_STATE',
      });
    } else {
      stateNames.set(s.name, s.line);
    }
  }

  // Check for duplicate function names
  const funcNames = new Map<string, number>();
  for (const f of contractInfo.functions) {
    const existing = funcNames.get(f.name);
    if (existing !== undefined) {
      diagnostics.push({
        line: f.line, column: 1, endLine: f.line, endColumn: 100,
        severity: 'error', category: 'analysis',
        message: `Duplicate function '${f.name}'. First declared on line ${existing}.`,
        code: 'MIST_DUPLICATE_FUNC',
      });
    } else {
      funcNames.set(f.name, f.line);
    }
  }

  // Check for duplicate event names
  const eventNames = new Map<string, number>();
  for (const e of contractInfo.events) {
    const existing = eventNames.get(e.name);
    if (existing !== undefined) {
      diagnostics.push({
        line: e.line, column: 1, endLine: e.line, endColumn: 100,
        severity: 'error', category: 'analysis',
        message: `Duplicate event '${e.name}'. First declared on line ${existing}.`,
        code: 'MIST_DUPLICATE_EVENT',
      });
    } else {
      eventNames.set(e.name, e.line);
    }
  }

  // Warn if no functions defined
  if (contractInfo.functions.length === 0) {
    diagnostics.push({
      line: 1, column: 1, endLine: 1, endColumn: 1,
      severity: 'warning', category: 'analysis',
      message: `Contract '${contractInfo.name}' has no functions defined. A contract needs at least one function to be useful.`,
      code: 'MIST_NO_FUNCTIONS',
    });
  }

  // Check for split percentage totals (simple heuristic scan)
  let inSplit = false;
  let splitLine = 0;
  let splitPercentages: number[] = [];
  for (let i = 0; i < lines.length; i++) {
    const stripped = lines[i].trim();
    if (stripped.startsWith('split ')) {
      inSplit = true;
      splitLine = i + 1;
      splitPercentages = [];
    } else if (inSplit && stripped === ']') {
      inSplit = false;
      const sum = splitPercentages.reduce((a, b) => a + b, 0);
      if (splitPercentages.length > 0 && sum !== 100) {
        diagnostics.push({
          line: splitLine, column: 1, endLine: i + 1, endColumn: stripped.length + 1,
          severity: 'error', category: 'analysis',
          message: `Split percentages sum to ${sum}%, but must equal exactly 100%. (MIST_E_SPLIT_PERCENTAGE)`,
          code: 'MIST_E_SPLIT_PERCENTAGE',
        });
      }
    } else if (inSplit) {
      const pctMatch = stripped.match(/(\d+)\s*%/);
      if (pctMatch) {
        splitPercentages.push(parseInt(pctMatch[1], 10));
      }
    }
  }
}

/* ================================================================== */
/*  Deploy preflight checks                                           */
/* ================================================================== */

export interface DeployPreflightContext {
  fileName: string;
  hasWallet: boolean;
  hasActiveAccount: boolean;
  projectHasMistFiles: boolean;
  network: string;
}

function runDeployPreflight(ctx: DeployPreflightContext | undefined, diagnostics: MistDiagnostic[]): void {
  if (!ctx) return;

  if (!ctx.fileName.endsWith('.mist')) {
    diagnostics.push({
      line: 1, column: 1, endLine: 1, endColumn: 1,
      severity: 'error', category: 'deploy',
      message: `Only '.mist' source files are deployable in Rivellum custom development mode. This file (${ctx.fileName}) cannot be deployed. Mist is the only deployable contract language.`,
      code: 'MIST_NOT_DEPLOYABLE',
    });
  }

  if (!ctx.projectHasMistFiles) {
    diagnostics.push({
      line: 1, column: 1, endLine: 1, endColumn: 1,
      severity: 'error', category: 'deploy',
      message: `This project contains no '.mist' files. Add a Mist contract source file to deploy custom logic.`,
      code: 'MIST_NO_ENTRY',
    });
  }

  if (!ctx.hasWallet) {
    diagnostics.push({
      line: 1, column: 1, endLine: 1, endColumn: 1,
      severity: 'warning', category: 'deploy',
      message: `Natos wallet is not connected. Connect your wallet to deploy contracts.`,
      code: 'MIST_NO_WALLET',
    });
  }

  if (!ctx.hasActiveAccount) {
    diagnostics.push({
      line: 1, column: 1, endLine: 1, endColumn: 1,
      severity: 'warning', category: 'deploy',
      message: `No active wallet account. Select an account in the Natos extension to deploy.`,
      code: 'MIST_NO_ACCOUNT',
    });
  }
}

/* ================================================================== */
/*  File-level checks                                                 */
/* ================================================================== */

function runFileChecks(source: string, fileName: string, diagnostics: MistDiagnostic[]): void {
  if (!fileName.endsWith('.mist')) return;

  const stripped = source.trim();
  if (stripped.length === 0) {
    diagnostics.push({
      line: 1, column: 1, endLine: 1, endColumn: 1,
      severity: 'info', category: 'syntax',
      message: 'Empty Mist file. Start with a contract declaration: contract MyContract',
      code: 'MIST_EMPTY',
    });
    return;
  }

  // Check that file starts with a contract
  const firstNonComment = stripped.split('\n').find(l => l.trim().length > 0 && !l.trim().startsWith('#'));
  if (firstNonComment && !firstNonComment.trim().startsWith('contract ')) {
    diagnostics.push({
      line: 1, column: 1, endLine: 1, endColumn: 1,
      severity: 'error', category: 'syntax',
      message: `A Mist file must begin with a 'contract' declaration. Found '${firstNonComment.trim().split(' ')[0]}' instead.`,
      code: 'MIST_NO_CONTRACT_ROOT',
    });
  }

  // Check for multiple contract declarations
  const contractLines = stripped.split('\n')
    .map((l, i) => ({ line: i + 1, text: l.trim() }))
    .filter(l => /^contract\s+/.test(l.text));
  if (contractLines.length > 1) {
    for (let i = 1; i < contractLines.length; i++) {
      diagnostics.push({
        line: contractLines[i].line, column: 1,
        endLine: contractLines[i].line, endColumn: contractLines[i].text.length + 1,
        severity: 'error', category: 'syntax',
        message: `Only one contract per file is supported. Found additional contract at line ${contractLines[i].line}.`,
        code: 'MIST_MULTIPLE_CONTRACTS',
      });
    }
  }
}

/* ================================================================== */
/*  Public API: validate                                              */
/* ================================================================== */

export interface ValidateOptions {
  /** Source code to validate. */
  source: string;
  /** File name (for extension / deploy checks). */
  fileName: string;
  /** Optional deploy preflight context. */
  deployContext?: DeployPreflightContext;
}

export interface ValidationResult {
  diagnostics: MistDiagnostic[];
  contractInfo: ContractInfo | null;
  /** Whether any errors exist (severity === 'error'). */
  hasErrors: boolean;
  /** Whether the source is deployable (Mist file, parsed, no errors). */
  isDeployable: boolean;
}

/**
 * Validate Mist source through the full diagnostic pipeline:
 *  1. Lexer → syntax diagnostics
 *  2. Parser → structural diagnostics
 *  3. Statement validation → body-level heuristics
 *  4. Static analysis → duplicates, split math, etc.
 *  5. File-level checks → empty, no contract, multiple contracts
 *  6. Deploy preflight (if context provided)
 */
export function validate(options: ValidateOptions): ValidationResult {
  const { source, fileName, deployContext } = options;
  const diagnostics: MistDiagnostic[] = [];

  // Only run Mist-specific analysis on .mist files
  const isMistFile = fileName.endsWith('.mist');

  if (isMistFile) {
    // Layer A: tokenize
    const { tokens, errors: lexErrors } = tokenize(source);
    diagnostics.push(...lexErrors);

    // Layer A+B: parse
    const ctx: ParseContext = { tokens, pos: 0, errors: [] };
    const contractInfo = parseContract(ctx);
    diagnostics.push(...ctx.errors);

    // Layer B: statement-level checks
    validateFunctionBodies(source, diagnostics);

    // Layer C: static analysis
    runStaticAnalysis(source, contractInfo, diagnostics);

    // File-level
    runFileChecks(source, fileName, diagnostics);

    // Layer D: deploy preflight
    if (deployContext) {
      runDeployPreflight(deployContext, diagnostics);
    }

    const hasErrors = diagnostics.some(d => d.severity === 'error');

    return {
      diagnostics,
      contractInfo,
      hasErrors,
      isDeployable: isMistFile && !hasErrors,
    };
  }

  // Non-mist files: only deploy checks
  if (deployContext) {
    runDeployPreflight(deployContext, diagnostics);
  }

  return {
    diagnostics,
    contractInfo: null,
    hasErrors: diagnostics.some(d => d.severity === 'error'),
    isDeployable: false,
  };
}

/* ================================================================== */
/*  Public API: isDeployableFile                                      */
/* ================================================================== */

/** Returns true if the given filename represents a deployable Mist contract. */
export function isDeployableFile(fileName: string): boolean {
  return fileName.endsWith('.mist');
}
